import React, { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { QueryParamsConst } from '../../../../constants/query-params.constants';
import Dropdown, { TDropdownProps } from '../../../@shared/filters/components/dropdown';
import DictionaryTerm from '../../../@shared/dictionaryTerm';
import Icon from '../../../@shared/icon';
import Search from '../search';
import { iconSize } from '../../../../constants/icon.constants';
import { DownloadBrochureListingModel } from '../../models/downloadBrochureListing.model';
import { TDownloadBrochureFilter, TDownloadBrochureListingFilters } from '../../types/downloadBrochures.types';
import './styles.scss';

const DesktopFilter: FC<{ model: DownloadBrochureListingModel; selectedFilters: TDownloadBrochureListingFilters }> = ({
  model,
  selectedFilters,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { CATEGORIES, LINE, SERIES } = QueryParamsConst;

  const getDropdownOptions = (filter?: TDownloadBrochureFilter, titleDictionaryKey?: string): TDropdownProps['dropdownOptions'] => {
    if (!filter) return [];
    return [
      { id: filter.filterType, title: t(titleDictionaryKey || '') },
      ...(filter?.value?.map((value) => ({ id: value, title: value })) || []),
    ];
  };

  const handleOptionClick = (ind: number, queryKey: string, options: TDropdownProps['dropdownOptions']) => {
    const filterValue = ind === 0 ? '' : (options?.[ind].id as string);
    const query =
      queryKey == CATEGORIES
        ? { categories: filterValue, line: '', series: '' }
        : queryKey == LINE
        ? { categories: '', line: filterValue, series: '' }
        : { series: filterValue };
    model.updateQueryByBulkKeys(navigate, query);
    model.resetSearch(navigate);
  };

  const dropDownsProps = [
    {
      filter: model.findFilterByName('categories'),
      queryKey: CATEGORIES,
      titleDictionaryKey: 'cnhi-download-brochure-listing-all-categories',
      id: 'categories-dropdown',
      hidden: false,
    },
    {
      filter: model.findFilterByName('line'),
      queryKey: LINE,
      titleDictionaryKey: 'cnhi-download-brochure-listing-all-lines',
      id: 'line-dropdown',
      hidden: false,
    },
    {
      filter: model.findFilterByName('series'),
      queryKey: SERIES,
      titleDictionaryKey: 'cnhi-download-brochure-listing-all-series',
      id: 'series-dropdown',
      hidden: !selectedFilters[LINE],
    },
  ];

  const shouldShowResetFilter = selectedFilters[CATEGORIES] !== '' || selectedFilters[LINE] !== '' || selectedFilters[SERIES] !== '';
  const resetFilterCTA = shouldShowResetFilter && (
    <span className="product-download-brochure-listing-with-filter__reset" onClick={() => model.resetFilter(navigate)}>
      <DictionaryTerm keyword="cnhi-download-brochure-listing-reset-filters" />
      <Icon iconName="action-refresh" size={iconSize['dsk']} color="primary" />
    </span>
  );

  return (
    <div className="product-download-brochure-listing-with-filter--dsk">
      <div className="product-download-brochure-listing-with-filter__heading">
        <p className="product-download-brochure-listing-with-filter__title">
          <DictionaryTerm keyword="cnhi-download-brochure-listing-filter-heading" />
        </p>
        {resetFilterCTA}
      </div>
      <div className="download-brochure-listing-filter">
        <div className="download-brochure-listing-filter__dropdown-wrapper">
          {dropDownsProps.map(({ filter, queryKey, id, titleDictionaryKey, hidden }) => {
            const options = getDropdownOptions(filter, titleDictionaryKey);
            if (!options?.length || hidden) return null;
            return (
              <Dropdown
                key={id}
                dropdownOptions={options}
                id={id}
                handleOptionClick={(ind) => handleOptionClick(ind, queryKey, options)}
                selectedOptionInd={(filter?.value?.findIndex((val) => val === selectedFilters[queryKey]) ?? -1) + 1}
              />
            );
          })}
        </div>
        <div className="download-brochure-listing__search">
          <Search model={model} selectedFilters={selectedFilters} />
        </div>
      </div>
    </div>
  );
};

export default DesktopFilter;
